import React, { createContext, useContext, useState, useEffect } from 'react';
// Tasand C: ainult EN on bundle'itud staatiliselt (fallback + kohene esmarender).
// Iga teine keel laetakse dünaamiliselt src/locales/<lang>.js eraldi chunkina,
// nii et kasutaja laeb ainult oma keele (~40 KB), mitte kõiki 22 (~1,36 MB).
import en from './locales/en';

const LanguageContext = createContext(null);

const SUPPORTED = ['et','en','ru','pl','cs','da','de','fi','fr','lv','lt','nl','no','sv','it','pt','es','hu','ro','sk','el','tl'];

// Laetud keelte vahemälu (en on alati olemas). Väldib sama keele korduvat importi.
const localeCache = { en };
function loadLocale(lang) {
  if (localeCache[lang]) return Promise.resolve(localeCache[lang]);
  // Dünaamiline import — webpack teeb igast locales/*.js failist eraldi chunki.
  // webpackChunkName "locale-[request]" annab ennustatava failinime (locale-tl.HASH.chunk.js),
  // et build-multilingual.js saaks õigele chunkile <link rel="preload"> osutada.
  return import(/* webpackChunkName: "locale-[request]" */ `./locales/${lang}`).then((mod) => {
    localeCache[lang] = mod.default;
    return mod.default;
  });
}

// Võtmetee kõndimine objektis (nt 'landing.h1Line1')
function walk(obj, keys) {
  let v = obj;
  for (const k of keys) v = v == null ? undefined : v[k];
  return v;
}

function detectInitialLang() {
  // 1. URL query parameter ?lang=XX — KÕRGEIM prioriteet (FB ad linkidele)
  // Ainus võimalus FB in-app brauseris keele kindlasti seada, kuna navigator.languages
  // võib olla tühi või tagastada valed väärtused (en-US default).
  try {
    const qp = new URLSearchParams(window.location.search).get('lang');
    if (qp && SUPPORTED.includes(qp.toLowerCase())) {
      const code = qp.toLowerCase();
      localStorage.setItem('language', code);
      console.log(`[lang] source: url, value: ${code}`);
      return code;
    } else if (qp) {
      console.log(`[lang] url param '${qp}' is not supported, falling through`);
    }
  } catch (e) { /* URLSearchParams puudu vanas brauseris */ }

  // 2. URL prefiks (/de/, /et/ jne) — Google maandumislehed
  const pathLang = window.location.pathname.split('/')[1];
  if (SUPPORTED.includes(pathLang)) {
    localStorage.setItem('language', pathLang);
    console.log(`[lang] source: url-path, value: ${pathLang}`);
    return pathLang;
  }
  // 3. window.__INITIAL_LANG__ — post-build skripti poolt seatud
  if (window.__INITIAL_LANG__ && SUPPORTED.includes(window.__INITIAL_LANG__)) {
    console.log(`[lang] source: window.__INITIAL_LANG__, value: ${window.__INITIAL_LANG__}`);
    return window.__INITIAL_LANG__;
  }
  // 4. localStorage (kasutaja varem salvestatud valik)
  const stored = localStorage.getItem('language');
  if (stored && SUPPORTED.includes(stored)) {
    console.log(`[lang] source: localStorage, value: ${stored}`);
    return stored;
  }
  // 5. Brauseri keel — käi läbi kõik kasutaja eelistatud keeled prioriteedi järjekorras
  const candidates = navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language || ''];
  for (const lang of candidates) {
    const code = lang.split('-')[0];
    if (SUPPORTED.includes(code)) {
      console.log(`[lang] source: navigator (${candidates.join(',') || 'empty'}), value: ${code}`);
      return code;
    }
  }
  console.log(`[lang] source: default (navigator was: ${candidates.join(',') || 'empty'}), value: en`);
  return 'en';
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => detectInitialLang());
  // messages = aktiivse keele tõlked. Init prioriteet:
  //  1) window.__INO_MSG — build-multilingual.js laadis selle SÜNKROONSELT ENNE main.js-i
  //     (õige keel kohe esimesel renderil → EN-flash kaob). Kasutame ainult kui keelemarker
  //     klapib (turvakontroll), muidu jätkame vana käitumisega.
  //  2) vahemälu (kui keel juba laetud, nt pärast vahetust)
  //  3) EN — kohene fallback (turvavõrk: esmarender ei jää tühjaks ega kuva tooreid võtmeid)
  const [messages, setMessages] = useState(() => {
    if (typeof window !== 'undefined' && window.__INO_MSG && window.__INO_LANG === lang) {
      localeCache[lang] = window.__INO_MSG; // täida vahemälu, et effect ei re-impordiks
      return window.__INO_MSG;
    }
    return localeCache[lang] || en;
  });

  useEffect(() => {
    let cancelled = false;
    // Kui sünkroonne andmed olid juba olemas (window.__INO_MSG), on cache täidetud ja
    // loadLocale resolvib koheselt — uut imporeti ei tehta. Vajalik keele VAHETUSE jaoks.
    loadLocale(lang)
      .then((m) => { if (!cancelled) setMessages(m); })
      .catch(() => { if (!cancelled) setMessages(en); }); // laadimine ebaõnnestus → EN
    return () => { cancelled = true; };
  }, [lang]);

  // t: otsi aktiivsest keelest; kui võtit pole → EN fallback; kui sealgi pole → võti ise.
  // (Identne loogika vana translations.js t()-ga, lihtsalt EN on alati bundle'is.)
  const t = (key) => {
    const keys = key.split('.');
    const active = messages || en;
    const val = walk(active, keys);
    if (val !== undefined) return val;
    const fallback = walk(en, keys);
    return fallback !== undefined ? fallback : key;
  };

  const changeLang = (code) => {
    setLang(code);
    localStorage.setItem('language', code);
    console.log(`[lang] source: user-select, value: ${code}`);
  };

  return (
    <LanguageContext.Provider value={{ lang, changeLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useTranslation = () => useContext(LanguageContext);
